import React, { useEffect, useState, useRef } from "react";
import "./CVPage.css";
import Header from "./Header";
import Footer from "./Footer";
import ScrollArrow from "./ScrollArrow";
import { cvData } from "../data/cvData";

export default function CVPage() {
  const [isPageLoaded, setIsPageLoaded] = useState(false);

  // 3D Movement functions for achievement cards (same as testimonial cards)
  const handleAchievementCardMouseMove = (e, cardElement) => {
    if (!cardElement) return;

    const rect = cardElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -10; // Max 10deg tilt
    const rotateY = ((x - centerX) / centerX) * 10;

    // Calculate shine position (0-100%)
    const shineX = (x / rect.width) * 100;
    const shineY = (y / rect.height) * 100;

    // Calculate dynamic shadow offset based on cursor position
    const shadowOffsetX = -((x - centerX) / centerX) * 6; // Max 6px offset
    const shadowOffsetY = -((y - centerY) / centerY) * 6; // Max 6px offset

    cardElement.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.005)`;

    // Update CSS custom properties for shine effect
    cardElement.style.setProperty("--shine-x", `${shineX}%`);
    cardElement.style.setProperty("--shine-y", `${shineY}%`);
    cardElement.style.setProperty("--shine-opacity", "0.7");

    // Update CSS custom properties for dynamic shadows
    cardElement.style.setProperty("--shadow-x", `${shadowOffsetX}px`);
    cardElement.style.setProperty("--shadow-y", `${shadowOffsetY}px`);
  };

  const handleAchievementCardMouseEnter = () => {
    // Dispatch custom event to notify header
    window.dispatchEvent(new CustomEvent("cardHoverStart"));
  };

  const handleAchievementCardMouseLeave = (cardElement) => {
    if (!cardElement) return;

    cardElement.style.transform =
      "perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)";
    cardElement.style.setProperty("--shine-opacity", "0");
    cardElement.style.setProperty("--shadow-x", "0px");
    cardElement.style.setProperty("--shadow-y", "0px");

    // Dispatch custom event to notify header
    window.dispatchEvent(new CustomEvent("cardHoverEnd"));
  };

  // 3D Movement functions for skill tags (same as tech cards)
  const handleSkillTagMouseMove = (e, tagElement) => {
    if (!tagElement) return;

    const rect = tagElement.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    const rotateX = ((y - centerY) / centerY) * -8; // Max 8deg tilt
    const rotateY = ((x - centerX) / centerX) * 8;

    // Calculate shine position (0-100%)
    const shineX = (x / rect.width) * 100;
    const shineY = (y / rect.height) * 100;

    // Calculate dynamic shadow offset based on cursor position
    const shadowOffsetX = -((x - centerX) / centerX) * 4; // Max 4px offset
    const shadowOffsetY = -((y - centerY) / centerY) * 4; // Max 4px offset

    tagElement.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;

    // Update CSS custom properties for shine effect
    tagElement.style.setProperty("--shine-x", `${shineX}%`);
    tagElement.style.setProperty("--shine-y", `${shineY}%`);
    tagElement.style.setProperty("--shine-opacity", "0.4");

    // Update CSS custom properties for dynamic shadows
    tagElement.style.setProperty("--shadow-x", `${shadowOffsetX}px`);
    tagElement.style.setProperty("--shadow-y", `${shadowOffsetY}px`);
  };

  const handleSkillTagMouseLeave = (tagElement) => {
    if (!tagElement) return;

    tagElement.style.transform =
      "perspective(800px) rotateX(0deg) rotateY(0deg) scale(1)";
    tagElement.style.setProperty("--shine-opacity", "0");
    tagElement.style.setProperty("--shadow-x", "0px");
    tagElement.style.setProperty("--shadow-y", "0px");
  };

  // Add body class for page styling
  useEffect(() => {
    document.body.classList.add("cv-page");
    return () => {
      document.body.classList.remove("cv-page");
    };
  }, []);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Page load animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPageLoaded(true);
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  // Auto-scroll to content after 4 seconds (only if still at top)
  useEffect(() => {
    const autoScrollTimer = setTimeout(() => {
      // Check if user is still at the top of the page
      const currentScrollPosition =
        window.pageYOffset || document.documentElement.scrollTop;

      // Only auto-scroll if user hasn't manually scrolled (within 50px of top)
      if (currentScrollPosition <= 50) {
        const contentElement = document.querySelector(".cv-content");
        if (contentElement) {
          const elementTop = contentElement.offsetTop;
          const remInPixels = parseFloat(
            getComputedStyle(document.documentElement).fontSize
          );
          const targetTop = elementTop - 2 * remInPixels; // 2rem before the element

          // Custom slow scroll function
          const startTop = window.pageYOffset;
          const distance = targetTop - startTop;
          const duration = 2000; // 2 seconds for slower scroll
          let startTime = null;

          const slowScroll = (currentTime) => {
            if (startTime === null) startTime = currentTime;
            const timeElapsed = currentTime - startTime;
            const progress = Math.min(timeElapsed / duration, 1);

            // Easing function for smooth animation (ease-out)
            const ease = 1 - Math.pow(1 - progress, 3);

            window.scrollTo(0, startTop + distance * ease);

            if (progress < 1) {
              requestAnimationFrame(slowScroll);
            }
          };

          requestAnimationFrame(slowScroll);
        }
      }
    }, 4000); // 4000ms = 4 seconds

    return () => clearTimeout(autoScrollTimer);
  }, []); // Run once on mount

  // Intersection Observer for scroll-based animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          } else {
            entry.target.classList.remove("visible");
          }
        });
      },
      {
        threshold: 0.08,
        rootMargin: "0px 0px -100px 0px",
      }
    );

    // Observe all sections
    const sections = document.querySelectorAll(".cv-content > *:not(.cv-hero)");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <Header />
      <div className={`cv-page-wrapper ${isPageLoaded ? "loaded" : ""}`}>
        <div className="cv-hero">
          <div className="cv-hero-content">
            <div className="cv-title">Curriculum Vitae</div>
            {/* <p className="cv-subtitle">
              Product Engineer with expertise in design and development
            </p> */}
          </div>
        </div>

        <div className="cv-content">
          {/* Personal Information */}
          <section className="cv-section personal-info">
            <div className="cv-section-container">
              <h2 className="section-eyebrow">Personal Information</h2>
              <div className="personal-info-content">
                <div className="personal-info-image">
                  <img src={cvData.personal.image} alt={cvData.personal.name} />
                </div>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="info-label">Name</span>
                    <span className="info-value">{cvData.personal.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Location</span>
                    <span className="info-value">
                      {cvData.personal.location}
                    </span>
                  </div>
                  {/* <div className="info-item">
                    <span className="info-label">Date of Birth</span>
                    <span className="info-value">
                      {cvData.personal.birthdate}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Place of Birth</span>
                    <span className="info-value">
                      {cvData.personal.birthplace}
                    </span>
                  </div> */}
                  <div className="info-item">
                    <span className="info-label">Email</span>
                    <span className="info-value">{cvData.personal.email}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Phone</span>
                    <span className="info-value">{cvData.personal.phone}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">LinkedIn</span>
                    <span className="info-value">
                      <a
                        href={cvData.personal.linkedin.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {cvData.personal.linkedin.display}
                      </a>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Professional Summary */}
          <section className="cv-section summary">
            <div className="cv-section-container">
              <h2 className="section-eyebrow">Professional Summary</h2>
              <p className="summary-text">{cvData.summary}</p>
            </div>
          </section>
          {/* Languages */}
          <section className="cv-section languages">
            <div className="cv-section-container">
              <h2 className="section-eyebrow">Languages</h2>

              <div className="languages-grid">
                {cvData.languages.map((language, index) => (
                  <div
                    key={index}
                    className="language-badge"
                    onMouseMove={(e) =>
                      handleSkillTagMouseMove(e, e.currentTarget)
                    }
                    onMouseLeave={(e) =>
                      handleSkillTagMouseLeave(e.currentTarget)
                    }
                  >
                    <div className="language-shine"></div>
                    <img
                      src={language.flag}
                      alt={`${language.name} flag`}
                      className="language-flag"
                    />
                    <div className="language-info">
                      <span className="language-name">{language.name}</span>
                      <span className="language-level">{language.level}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Skills */}
          <section className="cv-section skills">
            <div className="cv-section-container">
              <h2 className="section-eyebrow">Technical Skills</h2>

              <div className="skills-grid">
                {Object.entries(cvData.skills).map(([category, skills]) => (
                  <div key={category} className="skill-category">
                    <h3 className="skill-category-title">{category}</h3>
                    <div className="skill-tags">
                      {skills.map((skill, index) => (
                        <span
                          key={index}
                          className="skill-tag"
                          onMouseMove={(e) =>
                            handleSkillTagMouseMove(e, e.currentTarget)
                          }
                          onMouseLeave={(e) =>
                            handleSkillTagMouseLeave(e.currentTarget)
                          }
                        >
                          <div className="skill-tag-shine"></div>
                          <span className="skill-name">{skill}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Experience */}
          <section className="cv-section experience">
            <div className="cv-section-container">
              <h2 className="section-eyebrow">Professional Experience</h2>

              {cvData.experience.map((job, index) => (
                <div key={index} className="experience-item">
                  {job.image && (
                    <div className="experience-image">
                      <img src={job.image} alt={job.company} />
                    </div>
                  )}
                  <div className="experience-content">
                    <div className="experience-header">
                      <h3 className="job-title">{job.title}</h3>
                      <span className="company">{job.company}</span>
                      <span className="period">{job.period}</span>
                      {job.location && (
                        <span className="location">{job.location}</span>
                      )}
                    </div>
                    <p className="job-description">{job.description}</p>
                    {job.achievements && (
                      <ul className="job-achievements-list">
                        {job.achievements.map(
                          (achievement, achievementIndex) => (
                            <li
                              key={achievementIndex}
                              className="job-achievement"
                            >
                              {achievement}
                            </li>
                          )
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Education */}
          <section className="cv-section education">
            <div className="cv-section-container">
              <h2 className="section-eyebrow">Education</h2>

              {cvData.education.map((education, index) => (
                <div key={index} className="education-item">
                  {education.image && (
                    <div className="education-image">
                      <img src={education.image} alt={education.institution} />
                    </div>
                  )}
                  <div className="education-content">
                    <div className="education-header">
                      <h3 className="degree">{education.degree}</h3>
                      <span className="institution">
                        {education.institution}
                      </span>
                      <span className="period">{education.period}</span>
                    </div>
                    <p className="education-description">
                      {education.description}
                    </p>
                    {education.achievements && (
                      <ul className="education-achievements-list">
                        {education.achievements.map(
                          (achievement, achievementIndex) => (
                            <li
                              key={achievementIndex}
                              className="education-achievement"
                            >
                              {achievement}
                            </li>
                          )
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Certificates */}
          <section className="cv-section certificates">
            <div className="cv-section-container">
              <h2 className="section-eyebrow">Certificates</h2>

              {cvData.certificates.map((certificate, index) => (
                <div key={index} className="certificate-item">
                  {certificate.image && (
                    <div className="certificate-image">
                      <img
                        src={certificate.image}
                        alt={certificate.institution}
                      />
                    </div>
                  )}
                  <div className="certificate-content">
                    <div className="certificate-header">
                      <h3 className="certificate-name">{certificate.name}</h3>
                      <span className="certificate-institution">
                        {certificate.institution}
                      </span>
                      <span className="period">{certificate.period}</span>
                    </div>
                    <p className="certificate-description">
                      {certificate.description}
                    </p>
                    {certificate.achievements && (
                      <ul className="certificate-achievements-list">
                        {certificate.achievements.map(
                          (achievement, achievementIndex) => (
                            <li
                              key={achievementIndex}
                              className="certificate-achievement"
                            >
                              {achievement}
                            </li>
                          )
                        )}
                      </ul>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Publications */}
          <section className="cv-section publications">
            <div className="cv-section-container">
              <h2 className="section-eyebrow">Publications</h2>

              {cvData.publications.map((publication, index) => (
                <div key={index} className="publication-item">
                  {publication.image && (
                    <div className="publication-image">
                      <img src={publication.image} alt={publication.title} />
                    </div>
                  )}
                  <div className="publication-content">
                    <div className="publication-header">
                      <h3 className="publication-title">
                        <a
                          href={publication.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {publication.title}
                        </a>
                      </h3>
                      <div className="publication-meta">
                        <span className="publication-type">
                          {publication.type}
                        </span>
                        <span className="publication-year">
                          {publication.year}
                        </span>
                      </div>
                    </div>
                    <p className="publication-description">
                      {publication.description}
                    </p>
                    {publication.keywords && (
                      <div className="publication-keywords">
                        {publication.keywords.map((keyword, keywordIndex) => (
                          <span key={keywordIndex} className="keyword-tag">
                            {keyword}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Achievements */}
          {/* <section className="cv-section achievements">
            <div className="cv-section-container">
              <h2 className="section-eyebrow">Key Achievements</h2>

              <div className="achievements-grid">
                {cvData.achievements.map((achievement, index) => (
                  <div
                    key={index}
                    className="achievement-item"
                    onMouseMove={(e) =>
                      handleAchievementCardMouseMove(e, e.currentTarget)
                    }
                    onMouseEnter={handleAchievementCardMouseEnter}
                    onMouseLeave={(e) =>
                      handleAchievementCardMouseLeave(e.currentTarget)
                    }
                  >
                    <div className="achievement-card-shine"></div>
                    <h3 className="achievement-title">{achievement.title}</h3>
                    <p className="achievement-description">
                      {achievement.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section> */}
        </div>
      </div>
      <Footer />
      <ScrollArrow />
    </>
  );
}
